import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { useErrorHandler, useExperiments, useHttpClient, useTranslation } from '@wix/fe-essentials/react';
import { useSetAtom } from 'jotai';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { fetchLatestScan } from '../api/fetch-latest-scan';
import { pollForFreshLatestScan } from '../api/poll-for-fresh-latest-scan';
import { queryExecutionResultByItemType } from '../api/query-execution-result-by-item-type';
import { queryRuleExecutionResults } from '../api/query-rule-execution-results';
import { resetAllHeadingStructureAIAtom } from '../atoms/heading-structure-ai-atoms';
import { autoScanTriggeredAtom, isWaitingForBmLatestScanAtom } from '../atoms/session-atoms';
import { useGetItemTypeEntitiesSummaryAfterCrossPageCalculation } from '../components/verticals-drill-in-utils/use-get-entity-summary-after-cross-page-calculation';
import { SITE_LEVEL_PAGE_ID } from '../consts';
import { isStaticPage } from '../rule-mappers/shared/helpers';
import { mapPageIdToPageDataFromServer } from '../utils/map-page-id-to-page-data-server';
import { resolveGroupPageIdsForBmScanJob } from '../utils/resolve-group-page-ids-for-bm-scan-job';
import { AnalysisState, usePanelStateAtom } from './rules-state';
import { useScanTimeoutManager } from './scan-timeout';
import { useA11yWizardAdapter } from './use-a11y-wizard-adapter';
import { useMapPageIdToPageDataFromServer } from './use-map-page-id-to-page-data-from-server';
import { useSessionInfoContext } from './use-session-info-context';
const filterResultsForJob = (results, jobId, scanDate) => results.filter(result => {
  var _result$metadata;
  const createdAt = result.dateCreated ? new Date(result.dateCreated) : undefined;
  return ((_result$metadata = result.metadata) == null ? void 0 : _result$metadata.analysisJobId) === jobId && createdAt != null && createdAt >= scanDate;
});
const buildGroupsDataMapForJobs = pagesData => {
  const groupsDataMap = new Map();
  for (const [pageId, {
    innerRoutes,
    itemType,
    title,
    tpaPage,
    tpaPageId
  }] of pagesData) {
    groupsDataMap.set(pageId, {
      analysisState: AnalysisState.FirstAnalyzing,
      failedPages: 0,
      isTpaPage: tpaPage,
      itemType,
      pageId,
      rulesStateMap: new Map(),
      scannedPages: 0,
      title,
      tpaPageId
    });
    if (innerRoutes) {
      for (const route of innerRoutes) {
        groupsDataMap.set(route.id ?? '', {
          analysisState: AnalysisState.FirstAnalyzing,
          failedPages: 0,
          isTpaPage: false,
          itemType,
          pageId: route.id ?? '',
          rulesStateMap: new Map(),
          scannedPages: 0,
          title: route.name ?? ''
        });
      }
    }
  }
  if (groupsDataMap.has(SITE_LEVEL_PAGE_ID)) {
    return groupsDataMap;
  }
  groupsDataMap.set(SITE_LEVEL_PAGE_ID, {
    analysisState: AnalysisState.FirstAnalyzing,
    failedPages: 0,
    isTpaPage: false,
    itemType: 'SITE_LEVEL',
    pageId: SITE_LEVEL_PAGE_ID,
    rulesStateMap: new Map(),
    scannedPages: 0,
    title: ''
  });
  return groupsDataMap;
};
const hydrateJobResults = async _ref => {
  let {
    job,
    scanDate,
    branchId,
    groupsDataMap,
    httpClient,
    errorHandler,
    setGroupExecutionResults,
    setEntitiesSummaryAfterCrossPageCalculation,
    getItemTypeEntitiesSummaryAfterCrossPageCalculation
  } = _ref;
  if (!job.itemType) {
    const executionResults = filterResultsForJob(await queryRuleExecutionResults(httpClient, errorHandler, 'SITE_LEVEL', 'SITE_LEVEL', branchId), job.jobId, scanDate);
    if (executionResults.length > 0 && groupsDataMap.has(SITE_LEVEL_PAGE_ID)) {
      setGroupExecutionResults(SITE_LEVEL_PAGE_ID, executionResults);
    }
    return;
  }
  const itemType = job.itemType;
  const executionResults = filterResultsForJob(await queryExecutionResultByItemType(httpClient, itemType, errorHandler), job.jobId, scanDate);
  if (executionResults.length === 0) {
    return;
  }
  const resultsGroupedByPageId = _.groupBy(executionResults, 'metadata.pageId');
  for (const [pageId, pageResults] of Object.entries(resultsGroupedByPageId)) {
    if (groupsDataMap.has(pageId)) {
      setGroupExecutionResults(pageId, pageResults);
    }
  }
  if (!isStaticPage(itemType)) {
    const pageIds = resolveGroupPageIdsForBmScanJob(job, groupsDataMap);
    const tpaPageId = pageIds.find(pageId => {
      var _groupsDataMap$get;
      return (_groupsDataMap$get = groupsDataMap.get(pageId)) == null ? void 0 : _groupsDataMap$get.isTpaPage;
    });
    if (tpaPageId) {
      const pageIdToStateMap = getItemTypeEntitiesSummaryAfterCrossPageCalculation(executionResults, tpaPageId);
      setEntitiesSummaryAfterCrossPageCalculation(pageIdToStateMap, itemType);
    }
  }
};
export function useQueryLatestScanOnOpen(_ref2) {
  let {
    isFirstFetching,
    analyzeFullSite
  } = _ref2;
  const {
    branchId,
    shouldQueryLatestScan
  } = useSessionInfoContext();
  const setAutoScanTriggered = useSetAtom(autoScanTriggeredAtom);
  const setIsWaitingForBmLatestScan = useSetAtom(isWaitingForBmLatestScanAtom);
  const resetAllHeadingStructureAI = useSetAtom(resetAllHeadingStructureAIAtom);
  const {
    initializePanelState,
    setEntitiesSummaryAfterCrossPageCalculation,
    setGroupExecutionResults,
    setGroupsAnalysisState,
    setPagesData
  } = usePanelStateAtom();
  const {
    setScanTimeoutForItemType,
    setPageScanTimeout
  } = useScanTimeoutManager();
  const {
    a11yWizardAdapter,
    capabilities
  } = useA11yWizardAdapter();
  const {
    i18n
  } = useTranslation();
  const {
    experiments
  } = useExperiments();
  const httpClient = useHttpClient();
  const errorHandler = useErrorHandler();
  const mapPageIdToPageDataFromServerV2 = useMapPageIdToPageDataFromServer();
  const getItemTypeEntitiesSummaryAfterCrossPageCalculation = useGetItemTypeEntitiesSummaryAfterCrossPageCalculation();
  const queryStartedRef = useRef(false);
  useEffect(() => {
    if (!shouldQueryLatestScan || isFirstFetching || queryStartedRef.current) {
      return;
    }
    queryStartedRef.current = true;
    setAutoScanTriggered(true);
    const queryLatestScan = async () => {
      setIsWaitingForBmLatestScan(true);
      try {
        var _latestScan$jobs;
        const latestScan = await pollForFreshLatestScan({
          fetchLatestScan: () => fetchLatestScan(httpClient, errorHandler)
        });
        if (!(latestScan != null && (_latestScan$jobs = latestScan.jobs) != null && _latestScan$jobs.length)) {
          analyzeFullSite();
          return;
        }
        setGroupsAnalysisState(AnalysisState.FirstAnalyzing, undefined, true);
        const useCardsUi = experiments.enabled('specs.a11y-wizard.useCardsUI');
        const pagesData = capabilities.compactPanelChrome || useCardsUi ? await mapPageIdToPageDataFromServerV2() : await mapPageIdToPageDataFromServer(a11yWizardAdapter, i18n, httpClient, errorHandler);
        await Promise.all([a11yWizardAdapter.reloadSiteProperties(), setPagesData(pagesData)]);
        const groupsDataMap = buildGroupsDataMapForJobs(pagesData);
        initializePanelState(latestScan.jobs);
        resetAllHeadingStructureAI();
        for (const job of latestScan.jobs) {
          if (job.itemType && job.itemType !== ITEM_TYPES.STATIC_PAGE_V2) {
            setScanTimeoutForItemType(job.itemType);
          }
        }
        setScanTimeoutForItemType(ITEM_TYPES.STATIC_PAGE_V2);
        setPageScanTimeout(SITE_LEVEL_PAGE_ID);
        for (const job of latestScan.jobs) {
          await hydrateJobResults({
            branchId: branchId ?? undefined,
            errorHandler,
            getItemTypeEntitiesSummaryAfterCrossPageCalculation,
            groupsDataMap,
            httpClient,
            job,
            scanDate: latestScan.scanDate,
            setEntitiesSummaryAfterCrossPageCalculation,
            setGroupExecutionResults
          });
        }
      } catch {
        setGroupsAnalysisState(AnalysisState.AnalyzeFailed, undefined, true);
        analyzeFullSite();
      } finally {
        setIsWaitingForBmLatestScan(false);
      }
    };
    void queryLatestScan();
  }, [analyzeFullSite, a11yWizardAdapter, branchId, capabilities.compactPanelChrome, errorHandler, experiments, getItemTypeEntitiesSummaryAfterCrossPageCalculation, httpClient, i18n, initializePanelState, isFirstFetching, mapPageIdToPageDataFromServerV2, resetAllHeadingStructureAI, setAutoScanTriggered, setEntitiesSummaryAfterCrossPageCalculation, setGroupExecutionResults, setGroupsAnalysisState, setIsWaitingForBmLatestScan, setPageScanTimeout, setPagesData, setScanTimeoutForItemType, shouldQueryLatestScan]);
}
//# sourceMappingURL=use-query-latest-scan-on-open.js.map